import type {
  AnalyticsEventInput,
  AvailabilityRequest,
  AvailabilityResponse,
  Booking,
  BookingInput,
  Branch,
  GarageConfig,
  GarageHiveOptionsRequest,
  GarageHiveOptionsResponse,
  Lead,
  LeadInput,
  LeadUpdate,
  Service,
  VehicleLookupResult,
  TyreSearch,
  TyreSearchResult,
  TyreSelection,
  TyreQuote,
  TyreOrderResult,
} from "@autopic/shared";
import { ADMIN_TOKEN_STORAGE_KEY } from "@autopic/shared";

const API_BASE = (
  import.meta.env.VITE_API_BASE ?? "http://localhost:4000"
).replace(/\/$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

function getAdminToken(): string | null {
  try {
    return localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal } = opts;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  const adminToken = getAdminToken();
  if (adminToken) headers.Authorization = `Bearer ${adminToken}`;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    if ((err as Error)?.name === "AbortError") throw err;
    throw new ApiError(
      0,
      "network_error",
      "Couldn't reach the booking service.",
    );
  }

  if (res.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!res.ok) {
    const e = (
      payload as { error?: { code?: string; message?: string } } | null
    )?.error;
    throw new ApiError(
      res.status,
      e?.code ?? "error",
      e?.message ?? `Request failed (${res.status}).`,
    );
  }

  return payload as T;
}

export const api = {
  base: API_BASE,

  searchTyres(garageId: string, body: TyreSearch) {
    return request<TyreSearchResult>(
      `/api/garages/${encodeURIComponent(garageId)}/tyres/test/search`,
      { method: "POST", body },
    );
  },
  quoteTyres(garageId: string, body: TyreSelection) {
    return request<TyreQuote>(
      `/api/garages/${encodeURIComponent(garageId)}/tyres/test/quotes`,
      { method: "POST", body },
    );
  },
  orderTyres(garageId: string, id: string) {
    return request<TyreOrderResult>(
      `/api/garages/${encodeURIComponent(garageId)}/tyres/test/orders/${encodeURIComponent(id)}`,
      { method: "POST" },
    );
  },
  tyreOrderStatus(garageId: string, id: string) {
    return request<{
      reference: string;
      status: string;
      result: TyreOrderResult | null;
    }>(
      `/api/garages/${encodeURIComponent(garageId)}/tyres/test/orders/${encodeURIComponent(id)}`,
    );
  },

  health() {
    return request<{ ok: boolean }>("/health");
  },

  getConfig(garageId: string, signal?: AbortSignal) {
    return request<GarageConfig>(
      `/api/garages/${encodeURIComponent(garageId)}/config`,
      { signal },
    );
  },

  lookupVehicle(registration: string, garageId?: string, signal?: AbortSignal) {
    return request<VehicleLookupResult>("/api/vehicle/lookup", {
      method: "POST",
      body: { registration, ...(garageId ? { garageId } : {}) },
      signal,
    });
  },

  getServices(garageId: string, signal?: AbortSignal) {
    return request<Service[]>(
      `/api/garages/${encodeURIComponent(garageId)}/services`,
      { signal },
    );
  },

  getBranches(garageId: string, postcode?: string, signal?: AbortSignal) {
    const qs = postcode ? `?postcode=${encodeURIComponent(postcode)}` : "";
    return request<Branch[]>(
      `/api/garages/${encodeURIComponent(garageId)}/branches${qs}`,
      { signal },
    );
  },

  getAvailability(
    garageId: string,
    body: AvailabilityRequest,
    signal?: AbortSignal,
  ) {
    return request<AvailabilityResponse>(
      `/api/garages/${encodeURIComponent(garageId)}/availability`,
      { method: "POST", body, signal },
    );
  },

  getGarageHiveOptions(
    garageId: string,
    body: GarageHiveOptionsRequest,
    signal?: AbortSignal,
  ) {
    return request<GarageHiveOptionsResponse>(
      `/api/garages/${encodeURIComponent(garageId)}/garagehive-options`,
      { method: "POST", body, signal },
    );
  },

  createLead(body: LeadInput, signal?: AbortSignal) {
    return request<Lead>("/api/leads", { method: "POST", body, signal });
  },

  updateLead(id: string, body: LeadUpdate, signal?: AbortSignal) {
    return request<Lead>(`/api/leads/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
      signal,
    });
  },

  createBooking(body: BookingInput, signal?: AbortSignal) {
    return request<Booking>("/api/bookings", { method: "POST", body, signal });
  },

  sendAnalytics(body: AnalyticsEventInput) {
    // Fire-and-forget; keepalive so it survives navigation/unload.
    const url = `${API_BASE}/api/analytics/events`;
    const payload = JSON.stringify(body);
    const adminToken = getAdminToken();
    try {
      // sendBeacon cannot attach the admin Authorization header, so live admin
      // previews use fetch. Public sandbox sessions can retain beacon delivery.
      if (!adminToken && navigator.sendBeacon) {
        const blob = new Blob([payload], { type: "application/json" });
        if (navigator.sendBeacon(url, blob)) return Promise.resolve();
      }
    } catch {
      /* fall through to fetch */
    }
    return fetch(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
      },
      body: payload,
      keepalive: true,
    }).then(
      () => undefined,
      () => undefined,
    );
  },
};
